import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronDown, ChevronRight, User } from "lucide-react";
import { CATEGORIES, MY_PROFILE_ITEMS } from "../../data/communityCategories";
import { useAuth } from "../../context/AuthContext";
import AdmirersCard from "./AdmirersCard";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Icon({ name, className }) {
  const Cmp = Icons[name];
  if (!Cmp) return <Icons.Hash className={className} />;
  return <Cmp className={className} strokeWidth={1.7} />;
}

/**
 * 좌측 카테고리 사이드바 — Facebook 스타일
 * Props:
 *  - activeCategory: 현재 선택된 카테고리 id ('home' | 'relationships' | ...)
 *  - activeSub: 현재 선택된 서브카테고리 id (옵션)
 *  - onSelectCategory(catId, subId?)
 */
export default function CommunitySidebar({ activeCategory = "home", activeSub = null, onSelectCategory }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [openCats, setOpenCats] = useState({ [activeCategory]: true });
  const [commentUnread, setCommentUnread] = useState(0);
  const [messageUnread, setMessageUnread] = useState(0);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    const load = () => {
      fetch(`${API_URL}/api/notifications/unread-count?category=activity`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => (r.ok ? r.json() : { count: 0 })).then((d) => { if (alive) setCommentUnread(d.count || 0); }).catch(() => {});
      fetch(`${API_URL}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then((r) => (r.ok ? r.json() : { count: 0 })).then((d) => { if (alive) setMessageUnread(d.count ?? d.unread ?? 0); }).catch(() => {});
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [token]);

  const toggleCat = (id) => setOpenCats((m) => ({ ...m, [id]: !m[id] }));

  const handleClick = (cat, sub) => {
    if (cat.href) {
      navigate(cat.href);
      return;
    }
    if (cat.sub && !sub) {
      toggleCat(cat.id);
      onSelectCategory?.(cat.id, null);
      return;
    }
    onSelectCategory?.(cat.id, sub?.id || null);
  };

  return (
    <aside className="w-full lg:w-64 shrink-0" data-testid="community-sidebar">
      <div className="lg:sticky lg:top-20 space-y-1">
        {/* My Profile Card */}
        {user && (
          <div className="bg-white rounded-2xl border border-stone-100 p-3 mb-3 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
            <button
              onClick={() => navigate(`/timeline/${user.id}`)}
              className="w-full flex items-center gap-3 group"
              data-testid="sidebar-profile-toggle"
            >
              {user.profile_image ? (
                <img
                  src={user.profile_image.startsWith("http") ? user.profile_image : `${API_URL}${user.profile_image}`}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover border border-stone-100"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-burgundy-500 to-rose-500 text-white flex items-center justify-center font-bold">
                  {(user.username || "?").charAt(0)}
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <div className="font-semibold text-stone-900 text-sm truncate group-hover:text-burgundy-700">{user.username}</div>
                <div className="text-[11px] text-stone-500">내 프로필</div>
              </div>
            </button>
            <ul className="mt-3 space-y-0.5 pl-1">
              {/* 받은 관심 — 개인영역 최상단 (다시 방문 유도) */}
              <li>
                <AdmirersCard inline />
              </li>
              {MY_PROFILE_ITEMS.map((it) => (
                <li key={it.id}>
                  {it.id === "ai_friends" ? (
                    <Link
                      to={it.href}
                      className="block px-2 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 hover:bg-stone-50"
                      data-testid="sidebar-ai-friends"
                    >
                      <Icon name={it.icon || "Sparkles"} className="w-4 h-4 text-burgundy-700" />
                      <span className="animate-ai-text-shine">{it.label}</span>
                    </Link>
                  ) : it.href ? (
                    <Link
                      to={it.href}
                      className="px-2 py-2 text-sm text-stone-600 hover:text-burgundy-700 hover:bg-stone-50 rounded-lg flex items-center gap-2"
                      data-testid={`sidebar-item-${it.id}`}
                    >
                      <Icon name={it.icon || "Circle"} className="w-4 h-4" />
                      <span className="flex-1">{it.label}</span>
                      {it.id === "comment_alerts" && commentUnread > 0 && (
                        <span className="ml-auto min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="comment-alerts-badge">
                          {commentUnread > 9 ? "9+" : commentUnread}
                        </span>
                      )}
                      {it.id === "messages" && messageUnread > 0 && (
                        <span className="ml-auto min-w-[18px] h-[18px] px-1 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="messages-badge">
                          {messageUnread > 9 ? "9+" : messageUnread}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <button
                      onClick={() => {
                        if (it.id === "my_follows") navigate(`/timeline/${user.id}/followers`);
                        else if (it.id === "my_likes" || it.id === "my_media" || it.id === "my_activity") navigate(`/timeline/${user.id}`);
                      }}
                      className="w-full text-left block px-2 py-2 text-sm text-stone-600 hover:text-burgundy-700 hover:bg-stone-50 rounded-lg flex items-center gap-2"
                    >
                      <Icon name={it.icon || "Circle"} className="w-4 h-4" />
                      {it.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Categories (중앙 매거진과 중복되는 콘텐츠 카테고리는 사이드바에서 숨김) */}
        <nav className="bg-white rounded-2xl border border-stone-100 p-2 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
          {CATEGORIES.filter((c) => !c.hideInSidebar).map((cat) => {
            const isActive = activeCategory === cat.id && !activeSub;
            const isOpen = !!openCats[cat.id];
            const hasSub = !!(cat.sub && cat.sub.length);
            return (
              <div key={cat.id}>
                <button
                  onClick={() => handleClick(cat)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[14px] transition-colors ${
                    isActive
                      ? "bg-burgundy-50 text-burgundy-700 font-semibold"
                      : "text-stone-700 hover:bg-stone-50"
                  }`}
                  data-testid={`sidebar-cat-${cat.id}`}
                >
                  <Icon name={cat.icon} className={`w-5 h-5 ${isActive ? "text-burgundy-600" : "text-stone-500"}`} />
                  <span className="flex-1 text-left truncate">{cat.label}</span>
                  {hasSub && (isOpen ? <ChevronDown className="w-4 h-4 text-stone-400" /> : <ChevronRight className="w-4 h-4 text-stone-400" />)}
                </button>
                {hasSub && isOpen && (
                  <ul className="ml-8 pl-3 border-l border-stone-100 my-1 mb-2 space-y-0.5">
                    {cat.sub.map((s) => {
                      const subActive = activeCategory === cat.id && activeSub === s.id;
                      return (
                        <li key={s.id}>
                          <button
                            onClick={() => handleClick(cat, s)}
                            className={`w-full text-left px-3 py-1.5 text-[13px] rounded-lg transition-colors ${
                              subActive
                                ? "bg-burgundy-50 text-burgundy-700 font-medium"
                                : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                            }`}
                            data-testid={`sidebar-sub-${cat.id}-${s.id}`}
                          >
                            {s.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>
      </div>
    </aside>
  );
}
